import styled, { css } from "styled-components";
import { color, font, media, motion, radius, shadow } from "../../styles/theme";
import { ButtonSize, ButtonVariant } from "../types";

interface StyledProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fixedWidth?: boolean;
  $block?: boolean;
}

const variants = {
  primary: css`
    background: linear-gradient(180deg, ${color.bloodBright}, ${color.blood});
    color: ${color.white};
    border-color: ${color.bloodBright};
    box-shadow: ${shadow.lift};

    ${media.hover} {
      &:hover:not(:disabled) {
        background: linear-gradient(180deg, ${color.ember}, ${color.bloodBright});
        border-color: ${color.ember};
        box-shadow: ${shadow.emberGlow};
        transform: translateY(-2px);
      }
    }
  `,
  ghost: css`
    background: transparent;
    color: ${color.pale};
    border-color: ${color.ashLight};

    ${media.hover} {
      &:hover:not(:disabled) {
        border-color: ${color.bloodBright};
        color: ${color.white};
        background: ${color.bloodFaint};
        transform: translateY(-2px);
      }
    }
  `,
  quiet: css`
    background: transparent;
    color: ${color.smoke};
    border-color: transparent;
    padding-left: 0;
    padding-right: 0;

    ${media.hover} {
      &:hover:not(:disabled) {
        color: ${color.ember};
      }
    }
  `,
};

const sizes = {
  md: css`
    padding: 0.75rem 1.5rem;
    font-size: 0.8125rem;
  `,
  lg: css`
    padding: 1rem 2.25rem;
    font-size: 0.9375rem;
  `,
};

const base = css<StyledProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  font-family: ${font.display};
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  line-height: 1;
  border: 1px solid;
  border-radius: ${radius.md};
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: background ${motion.base} ${motion.easeOut},
    color ${motion.base} ${motion.easeOut},
    border-color ${motion.base} ${motion.easeOut},
    box-shadow ${motion.base} ${motion.easeOut},
    transform ${motion.base} ${motion.easeOut};

  ${({ $size }) => sizes[$size]}
  ${({ $variant }) => variants[$variant]}
  ${({ $fixedWidth }) => $fixedWidth && "min-width: 180px;"}
  ${({ $block }) => $block && "width: 100%;"}

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled,
  &[aria-disabled="true"] {
    opacity: 0.42;
    cursor: not-allowed;
    transform: none;
  }

  ${media.xs} {
    letter-spacing: 0.12em;
  }
`;

export const StyledButton = styled("button")<StyledProps>`
  ${base}
`;

export const StyledAnchor = styled("a")<StyledProps>`
  ${base}
`;
